import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import api from '../lib/api';
import { toast } from 'sonner';
import { Search, Check, X, Eye, RefreshCw, Edit, Bug } from 'lucide-react';

export default function Questions() {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDebugJson, setShowDebugJson] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [stores, setStores] = useState([]);
  const [editedAnswer, setEditedAnswer] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [quickAnswerDialog, setQuickAnswerDialog] = useState(null);
  const [quickAnswer, setQuickAnswer] = useState('');

  useEffect(() => {
    loadStores();
    loadQuestions();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
  };

  useEffect(() => {
    filterQuestions();
  }, [questions, searchTerm, statusFilter, storeFilter]);

  const loadStores = async () => {
    try {
      const response = await api.get('/settings/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Stores load error:', error);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await api.get('/questions');
      setQuestions(response.data);
    } catch (error) {
      toast.error('Sorular yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await api.post('/questions/refresh');
      if (response.data.success) {
        toast.success('Sorular güncelleniyor... Lütfen birkaç saniye bekleyin.');
        // Wait a bit then reload
        setTimeout(() => {
          loadQuestions();
          setRefreshing(false);
        }, 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yenileme başlatılamadı');
      console.error(error);
      setRefreshing(false);
    }
  };

  const filterQuestions = () => {
    let filtered = questions;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((q) => q.status === statusFilter);
    }

    if (storeFilter !== 'all') {
      filtered = filtered.filter((q) => q.store_id === storeFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (q) =>
          q.customer_question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.product.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredQuestions(filtered);
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.store_name : 'Bilinmeyen Mağaza';
  };

  const getStoreMode = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.mode : 'hybrid';
  };

  const isHybridMode = (storeId) => {
    return getStoreMode(storeId) === 'hybrid';
  };

  const getMarketplaceBadge = (marketplace) => {
    if (marketplace === 'trendyol') {
      return <Badge className="bg-orange-50 text-orange-700 border-orange-200">Trendyol</Badge>;
    } else if (marketplace === 'hepsiburada') {
      return <Badge className="bg-blue-50 text-blue-700 border-blue-200">Hepsiburada</Badge>;
    }
    return <Badge>{marketplace}</Badge>;
  };

  const handleApprove = async (question, customAnswer = null) => {
    try {
      const answerToSend = customAnswer || editedAnswer || question.ai_answer;
      
      // Validate answer exists and not empty
      if (!answerToSend || answerToSend.trim() === '') {
        toast.error('Lütfen bir cevap yazın veya AI cevabı oluşturun');
        return;
      }
      
      const response = await api.post(`/questions/${question.id}/approve`, {
        ai_answer: answerToSend,
      });
      
      // Check backend response
      if (response.data.success) {
        toast.success('✅ Cevap Trendyol\'a başarıyla gönderildi!');
        setSelectedQuestion(null);
        loadQuestions();
      } else {
        toast.error('❌ Cevap gönderilemedi: ' + (response.data.message || 'Bilinmeyen hata'));
        // Still refresh to show updated status
        loadQuestions();
      }
    } catch (error) {
      toast.error('Cevap gönderilirken hata oluştu: ' + (error.response?.data?.detail || error.message));
      console.error(error);
      loadQuestions();
    }
  };

  const handleQuickApprove = async (question, e) => {
    e.stopPropagation(); // Prevent opening detail view
    if (!question.ai_answer) {
      toast.error('AI cevap henüz oluşturulmamış');
      return;
    }
    await handleApprove(question, question.ai_answer);
  };

  const handleReject = async (question) => {
    try {
      await api.post(`/questions/${question.id}/reject`);
      toast.success('Soru reddedildi');
      setSelectedQuestion(null);
      loadQuestions();
    } catch (error) {
      toast.error('İşlem sırasında hata oluştu');
      console.error(error);
    }
  };

  const openQuickAnswerDialog = (question, e) => {
    e.stopPropagation(); // Prevent opening detail view
    setQuickAnswerDialog(question);
    setQuickAnswer('');
  };

  const handleQuickAnswerSubmit = async () => {
    if (!quickAnswer || quickAnswer.trim() === '') {
      toast.error('Lütfen bir cevap yazın');
      return;
    }
    
    try {
      const response = await api.post(`/questions/${quickAnswerDialog.id}/approve`, {
        ai_answer: quickAnswer,
      });
      
      // Check backend response
      if (response.data.success) {
        toast.success('✅ Cevap Trendyol\'a başarıyla gönderildi!');
        setQuickAnswerDialog(null);
        setQuickAnswer('');
        loadQuestions();
      } else {
        toast.error('❌ Cevap gönderilemedi: ' + (response.data.message || 'Bilinmeyen hata'));
        // Still close dialog and refresh to show updated status
        setQuickAnswerDialog(null);
        setQuickAnswer('');
        loadQuestions();
      }
    } catch (error) {
      toast.error('Cevap gönderilirken hata oluştu: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    }
  };

  const openDetail = (question) => {
    setSelectedQuestion(question);
    setEditedAnswer(question.ai_answer || '');
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Bekliyor' },
      sent: { className: 'bg-green-50 text-green-700 border-green-200', label: 'Gönderildi' },
      failed: { className: 'bg-red-50 text-red-700 border-red-200', label: 'Başarısız' },
      approved: { className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Onaylandı' },
      rejected: { className: 'bg-slate-50 text-slate-700 border-slate-200', label: 'Reddedildi' },
    };

    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const getAnswerTypeBadge = (answerType) => {
    if (!answerType) return null;
    
    const variants = {
      ai_auto: { className: 'bg-blue-50 text-blue-700 border-blue-200', icon: '🤖', label: 'AI Otomatik' },
      ai_auto_sent: { className: 'bg-green-50 text-green-700 border-green-200', icon: '✨', label: 'AI Full-Auto' },
      ai_regenerated: { className: 'bg-purple-50 text-purple-700 border-purple-200', icon: '🔄', label: 'AI Yenilendi' },
      ai_retried: { className: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: '↻', label: 'AI Tekrar' },
      manual: { className: 'bg-amber-50 text-amber-700 border-amber-200', icon: '👤', label: 'Manuel' },
    };

    const variant = variants[answerType];
    if (!variant) return null;
    
    return (
      <Badge className={variant.className + ' text-xs'}>
        {variant.icon} {variant.label}
      </Badge>
    );
  };

  const handleRetryAI = async (question) => {
    try {
      toast.info('AI cevap oluşturuluyor...');
      const response = await api.post(`/questions/${question.id}/retry-ai`);
      toast.success('AI cevap başarıyla oluşturuldu!');
      setEditedAnswer(response.data.ai_answer);
      loadQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI cevap oluşturulamadı');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>

            <h1 className="text-3xl font-bold mb-2">Sorular</h1>
            <p className="text-slate-600">Müşteri sorularını yönetin ve yanıtlayın</p>
          </div>
          <Button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="bg-[#FF6000] hover:bg-orange-600 text-white"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Güncelleniyor...' : 'Soruları Yenile'}
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Ürün veya soru ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="questions-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="questions-status-select">
                  <SelectValue placeholder="Durum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="pending">Bekliyor</SelectItem>
                  <SelectItem value="sent">Gönderildi</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="rejected">Reddedildi</SelectItem>
                  <SelectItem value="failed">Başarısız</SelectItem>
                </SelectContent>
              </Select>

              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="questions-store-select">
                  <SelectValue placeholder="Mağaza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Mağazalar</SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.store_name} ({store.marketplace === 'trendyol' ? 'Trendyol' : 'Hepsiburada'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Questions List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : filteredQuestions.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-600">Soru bulunamadı</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map((question) => (
              <Card
                key={question.id}
                className="border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => openDetail(question)}
                data-testid="questions-table-row"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {question.product.image && (
                      <img
                        src={question.product.image}
                        alt={question.product.title}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 line-clamp-1">{question.product.title}</h3>
                        <div className="flex gap-2 flex-shrink-0 flex-wrap">
                          {getMarketplaceBadge(question.marketplace)}
                          {getStatusBadge(question.status)}
                          {getAnswerTypeBadge(question.answer_type)}
                          {/* Debug button - only for harunketenc@gmail.com */}
                          {currentUser?.email === 'harunketenc@gmail.com' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDebugJson(question);
                              }}
                              className="h-6 px-2"
                            >
                              <Bug className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">{getStoreName(question.store_id)}</p>
                      <p className="text-sm text-slate-700 mb-2 font-medium">Soru: {question.customer_question}</p>
                      {question.ai_answer ? (
                        <p className="text-sm text-slate-600 line-clamp-2">Cevap: {question.ai_answer}</p>
                      ) : (question.ai_failed || question.status === 'failed' || !question.ai_answer) && question.status === 'pending' && (
                        <div className="text-sm">
                          <p className="text-amber-600 font-medium">⚠️ AI cevap oluşturamadı</p>
                          {question.ai_failure_reason && (
                            <p className="text-amber-500 text-xs mt-1">Sebep: {question.ai_failure_reason}</p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-slate-500">
                          {new Date(question.created_at).toLocaleString('tr-TR')}
                        </p>
                        <div className="flex gap-2">
                          {/* Quick approve button for hybrid mode with AI answer */}
                          {question.status === 'pending' && isHybridMode(question.store_id) && question.ai_answer && (
                            <Button
                              size="sm"
                              onClick={(e) => handleQuickApprove(question, e)}
                              className="bg-[#FF6000] hover:bg-orange-600 text-white h-8 px-3 text-xs"
                            >
                              <Check className="mr-1 h-3 w-3" />
                              Onayla
                            </Button>
                          )}
                          {/* Manual answer button when AI failed or no answer */}
                          {question.status === 'pending' && !question.ai_answer && (
                            <Button
                              size="sm"
                              onClick={(e) => openQuickAnswerDialog(question, e)}
                              className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
                            >
                              <Edit className="mr-1 h-3 w-3" />
                              Cevap Oluştur
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedQuestion} onOpenChange={() => setSelectedQuestion(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedQuestion && (
            <>
              <SheetHeader className="border-b pb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle>Soru Detayı</SheetTitle>
                  <div className="flex gap-2 items-center">
                    {getStatusBadge(selectedQuestion.status)}
                  </div>
                </div>
                {/* Quick action buttons at top */}
                {selectedQuestion.status === 'pending' && (
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="flex-1 bg-[#FF6000] hover:bg-orange-600 text-white"
                      onClick={() => handleApprove(selectedQuestion)}
                      data-testid="question-approve-button-top"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Onayla ve Gönder
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(selectedQuestion)}
                      data-testid="question-reject-button-top"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Reddet
                    </Button>
                  </div>
                )}
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Product Info */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Ürün Bilgileri</h3>
                  <div className="flex gap-4">
                    {selectedQuestion.product.image && (
                      <img
                        src={selectedQuestion.product.image}
                        alt={selectedQuestion.product.title}
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <p className="font-medium">{selectedQuestion.product.title}</p>
                      <p className="text-sm text-slate-600">{selectedQuestion.product.brand}</p>
                      {selectedQuestion.product.price > 0 && (
                        <p className="text-sm text-[#FF6000] font-semibold mt-1">{selectedQuestion.product.price} ₺</p>
                      )}
                    </div>
                  </div>

                  {/* Variants */}
                  {selectedQuestion.product.variants && Array.isArray(selectedQuestion.product.variants) && selectedQuestion.product.variants.length > 0 && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-sm font-medium mb-2">Varyantlar:</p>
                      <div className="space-y-1">
                        {selectedQuestion.product.variants.map((variant, idx) => {
                          if (!variant || typeof variant !== 'object') return null;
                          const attrs = variant.attributes || {};
                          return (
                            <p key={idx} className="text-sm text-slate-600">
                              {Object.entries(attrs)
                                .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
                                .join(', ')} {variant.stock ? `- ${variant.stock} adet` : ''}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Attributes */}
                  {selectedQuestion.product.attributes && typeof selectedQuestion.product.attributes === 'object' && (
                    (() => {
                      // Handle both array and object formats
                      let attrs = selectedQuestion.product.attributes;
                      let displayAttrs = {};
                      
                      if (Array.isArray(attrs)) {
                        // Convert array format to key-value pairs
                        attrs.forEach(attr => {
                          if (attr && typeof attr === 'object' && attr.attributeName && attr.attributeValue) {
                            displayAttrs[attr.attributeName] = attr.attributeValue;
                          }
                        });
                      } else if (typeof attrs === 'object') {
                        displayAttrs = attrs;
                      }
                      
                      const displayKeys = Object.keys(displayAttrs);
                      
                      if (displayKeys.length === 0) return null;
                      
                      return (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-sm font-medium mb-2">Ürün Özellikleri:</p>
                          <div className="grid grid-cols-1 gap-2">
                            {displayKeys.slice(0, 10).map((key) => {
                              const value = displayAttrs[key];
                              return (
                                <div key={key} className="text-sm">
                                  <span className="text-slate-600">{key}:</span>
                                  <span className="ml-1 font-medium">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                                </div>
                              );
                            })}
                            {displayKeys.length > 10 && (
                              <p className="text-xs text-slate-500 italic">+{displayKeys.length - 10} özellik daha...</p>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* Question */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Müşteri Sorusu</h3>
                  <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">{selectedQuestion.customer_question}</p>
                </div>

                {/* Product Description */}
                {selectedQuestion.product.description && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg">Ürün Açıklaması</h3>
                    <div 
                      className="text-sm text-slate-700 bg-slate-50 p-4 rounded-lg max-h-40 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: selectedQuestion.product.description }}
                    />
                  </div>
                )}

                {/* AI Answer */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">AI Cevabı</h3>
                    <div className="flex gap-2">
                      {/* Retry button for failed AI */}
                      {(selectedQuestion.ai_failed || !selectedQuestion.ai_answer || selectedQuestion.status === 'failed') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRetryAI(selectedQuestion)}
                          className="text-xs bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          AI Tekrar Dene
                        </Button>
                      )}
                      {/* Regular regenerate for pending questions */}
                      {selectedQuestion.status === 'pending' && selectedQuestion.ai_answer && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await api.post(`/questions/${selectedQuestion.id}/regenerate`);
                              setEditedAnswer(response.data.ai_answer);
                              toast.success('AI cevabı yeniden oluşturuldu!');
                            } catch (error) {
                              toast.error('Cevap yeniden oluşturulamadı');
                            }
                          }}
                          className="text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Yeniden Oluştur
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Show failure reason if AI failed */}
                  {(selectedQuestion.ai_failed || selectedQuestion.status === 'failed') && selectedQuestion.ai_failure_reason && (
                    <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">
                        <strong>AI Başarısızlık Sebebi:</strong> {selectedQuestion.ai_failure_reason}
                      </p>
                    </div>
                  )}
                  {/* Show info if AI couldn't answer but no error */}
                  {!selectedQuestion.ai_answer && !selectedQuestion.ai_failure_reason && selectedQuestion.status === 'pending' && (
                    <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Bilgi:</strong> AI bu soru için ürün bilgilerinde yeterli bilgi bulamadı. Lütfen manuel olarak cevap yazın.
                      </p>
                    </div>
                  )}
                  <Textarea
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    rows={6}
                    className="resize-none"
                    disabled={selectedQuestion.status !== 'pending' && selectedQuestion.status !== 'failed'}
                    placeholder={selectedQuestion.ai_failed || selectedQuestion.status === 'failed' || !selectedQuestion.ai_answer ? "Cevabınızı buraya yazabilirsiniz..." : "AI cevabını düzenleyebilirsiniz"}
                  />
                  <p className="text-xs text-slate-500">{editedAnswer.length} / 500 karakter</p>
                </div>

                {/* Actions */}
                {(selectedQuestion.status === 'pending' || selectedQuestion.status === 'failed') && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      className="flex-1 bg-[#FF6000] hover:bg-orange-600 text-white"
                      onClick={() => handleApprove(selectedQuestion)}
                      data-testid="question-approve-button"
                      disabled={!editedAnswer || editedAnswer.trim().length === 0}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Onayla ve Gönder
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleReject(selectedQuestion)}
                      data-testid="question-reject-button"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Reddet
                    </Button>
                  </div>
                )}

                {selectedQuestion.status !== 'pending' && selectedQuestion.status !== 'failed' && (
                  <div className="flex gap-2 items-center justify-center py-4 text-sm text-slate-600 border-t">
                    <Eye className="h-4 w-4" />
                    <span>Bu soru zaten işlendi</span>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Answer Dialog */}
      <Dialog open={!!quickAnswerDialog} onOpenChange={() => setQuickAnswerDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          {quickAnswerDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Manuel Cevap Oluştur</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Product Info */}
                <div className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  {quickAnswerDialog.product.image && (
                    <img
                      src={quickAnswerDialog.product.image}
                      alt={quickAnswerDialog.product.title}
                      className="w-16 h-16 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-1">{quickAnswerDialog.product.title}</p>
                    <p className="text-xs text-slate-600">{quickAnswerDialog.product.brand}</p>
                  </div>
                </div>

                {/* Question */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Müşteri Sorusu:</label>
                  <p className="text-sm text-slate-900 p-3 bg-slate-50 rounded-lg">
                    {quickAnswerDialog.customer_question}
                  </p>
                </div>

                {/* AI Failure Reason if exists */}
                {quickAnswerDialog.ai_failure_reason && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>AI Neden Başarısız Oldu:</strong> {quickAnswerDialog.ai_failure_reason}
                    </p>
                  </div>
                )}

                {/* Manual Answer Input */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Cevabınız: *</label>
                  <Textarea
                    value={quickAnswer}
                    onChange={(e) => setQuickAnswer(e.target.value)}
                    rows={6}
                    className="resize-none"
                    placeholder="Müşteriye gönderilecek cevabı yazın..."
                  />
                  <p className="text-xs text-slate-500">{quickAnswer.length} / 500 karakter</p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setQuickAnswerDialog(null)}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleQuickAnswerSubmit}
                  className="bg-[#FF6000] hover:bg-orange-600 text-white"
                  disabled={!quickAnswer || quickAnswer.trim() === ''}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Gönder
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Debug JSON Dialog - Only for harunketenc@gmail.com */}
      <Dialog open={!!showDebugJson} onOpenChange={() => setShowDebugJson(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>🔍 Debug - Trendyol JSON Verisi</DialogTitle>
          </DialogHeader>
          {showDebugJson && (
            <div className="space-y-4">
              {/* RAW Trendyol Data */}
              {showDebugJson.raw_trendyol_data && (
                <div>
                  <h3 className="font-bold text-lg mb-2 text-orange-600">🔥 Trendyol'dan Gelen Ham JSON</h3>
                  <div className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96">
                    <pre className="text-xs font-mono whitespace-pre-wrap">
                      {JSON.stringify(showDebugJson.raw_trendyol_data, null, 2)}
                    </pre>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-4 p-4 bg-orange-50 rounded">
                    <div>
                      <p className="font-semibold text-orange-700">productMainId (Ham):</p>
                      <p className="text-slate-600">{showDebugJson.raw_trendyol_data.productMainId || '❌ YOK'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-700">productContentId (Ham):</p>
                      <p className="text-slate-600">{showDebugJson.raw_trendyol_data.productContentId || '❌ YOK'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-700">id (Ham):</p>
                      <p className="text-slate-600">{showDebugJson.raw_trendyol_data.id || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-700">text (Ham):</p>
                      <p className="text-slate-600">{showDebugJson.raw_trendyol_data.text || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Processed Database Data */}
              <div>
                <h3 className="font-bold text-lg mb-2 text-blue-600">💾 Database'deki İşlenmiş Veri</h3>
                <div className="bg-slate-900 text-cyan-400 p-4 rounded-lg overflow-auto max-h-96">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(showDebugJson, null, 2)}
                  </pre>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mt-4 p-4 bg-blue-50 rounded">
                  <div>
                    <p className="font-semibold text-blue-700">External Question ID:</p>
                    <p className="text-slate-600">{showDebugJson.external_question_id}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">Product Content ID (DB):</p>
                    <p className="text-slate-600">{showDebugJson.product_content_id || '❌ YOK'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">Product Main ID (DB):</p>
                    <p className="text-slate-600">{showDebugJson.product?.id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">Ürün Title (DB):</p>
                    <p className="text-slate-600">{showDebugJson.product?.title || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Product Attributes & AI Context */}
              <div>
                <h3 className="font-bold text-lg mb-2 text-purple-600">🤖 AI Context - Ürün Bilgileri</h3>
                
                {/* Product Description */}
                <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                  <p className="font-semibold text-purple-700 mb-2">📝 Ürün Açıklaması:</p>
                  <div className="bg-white p-3 rounded border border-purple-200">
                    <p className="text-sm text-slate-700">
                      {showDebugJson.product?.description || '❌ AÇIKLAMA YOK'}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Uzunluk: {showDebugJson.product?.description?.length || 0} karakter
                    </p>
                  </div>
                </div>

                {/* Product Attributes */}
                <div className="mb-4 p-4 bg-green-50 rounded-lg">
                  <p className="font-semibold text-green-700 mb-2">🏷️ Ürün Özellikleri (Attributes):</p>
                  <div className="bg-white p-3 rounded border border-green-200">
                    {showDebugJson.product?.attributes && Object.keys(showDebugJson.product.attributes).length > 0 ? (
                      <div className="space-y-1">
                        {Object.entries(showDebugJson.product.attributes).map(([key, value]) => (
                          <div key={key} className="text-sm">
                            <span className="font-semibold text-green-700">{key}:</span>{' '}
                            <span className="text-slate-700">{value}</span>
                          </div>
                        ))}
                        <p className="text-xs text-green-600 mt-2 pt-2 border-t">
                          ✅ {Object.keys(showDebugJson.product.attributes).length} özellik bulundu
                        </p>
                      </div>
                    ) : (
                      <p className="text-red-600 font-semibold">❌ ATTRIBUTES BOŞ! AI cevap veremeyebilir.</p>
                    )}
                  </div>
                </div>

                {/* Product Variants */}
                <div className="mb-4 p-4 bg-yellow-50 rounded-lg">
                  <p className="font-semibold text-yellow-700 mb-2">🎨 Varyantlar:</p>
                  <div className="bg-white p-3 rounded border border-yellow-200">
                    {showDebugJson.product?.variants && showDebugJson.product.variants.length > 0 ? (
                      <div className="space-y-2">
                        {showDebugJson.product.variants.slice(0, 5).map((variant, idx) => (
                          <div key={idx} className="text-xs bg-yellow-50 p-2 rounded">
                            <p>
                              <span className="font-semibold">Beden:</span> {variant.attributes?.Beden || 'N/A'}{' '}
                              <span className="font-semibold">Renk:</span> {variant.attributes?.Renk || 'N/A'}{' '}
                              <span className="font-semibold">Stok:</span> {variant.stock || 0}
                            </p>
                          </div>
                        ))}
                        {showDebugJson.product.variants.length > 5 && (
                          <p className="text-xs text-yellow-600">
                            ... ve {showDebugJson.product.variants.length - 5} varyant daha
                          </p>
                        )}
                        <p className="text-xs text-yellow-600 mt-2 pt-2 border-t">
                          ✅ Toplam {showDebugJson.product.variants.length} varyant
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-600">Varyant bilgisi yok</p>
                    )}
                  </div>
                </div>

                {/* AI Answer Status */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-700 mb-2">💬 AI Cevap Durumu:</p>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="font-semibold">Cevap:</span>{' '}
                      <span className={showDebugJson.ai_answer ? 'text-green-600' : 'text-red-600'}>
                        {showDebugJson.ai_answer || '❌ CEVAP YOK'}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold">Status:</span>{' '}
                      <span className={`font-semibold ${
                        showDebugJson.status === 'sent' ? 'text-green-600' : 
                        showDebugJson.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                      }`}>
                        {showDebugJson.status}
                      </span>
                    </div>
                    {showDebugJson.error_message && (
                      <div>
                        <span className="font-semibold text-red-600">Hata:</span>{' '}
                        <span className="text-slate-700">{showDebugJson.error_message}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Context Test */}
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="font-semibold text-indigo-700 mb-2">🧪 AI Context Test:</p>
                  <div className="bg-white p-3 rounded border border-indigo-200">
                    <p className="text-sm text-slate-700 mb-2">
                      AI şu bilgileri görüyor ve cevap verirken kullanıyor:
                    </p>
                    <ul className="text-xs space-y-1 text-slate-600">
                      <li>✅ Ürün Adı: {showDebugJson.product?.title || 'YOK'}</li>
                      <li>✅ Marka: {showDebugJson.product?.brand || 'YOK'}</li>
                      <li className={showDebugJson.product?.description ? 'text-green-600' : 'text-red-600'}>
                        {showDebugJson.product?.description ? '✅' : '❌'} Açıklama: {showDebugJson.product?.description?.length || 0} karakter
                      </li>
                      <li className={
                        showDebugJson.product?.attributes && Object.keys(showDebugJson.product.attributes).length > 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }>
                        {showDebugJson.product?.attributes && Object.keys(showDebugJson.product.attributes).length > 0 ? '✅' : '❌'} 
                        Attributes: {showDebugJson.product?.attributes ? Object.keys(showDebugJson.product.attributes).length : 0} özellik
                      </li>
                      <li className={
                        showDebugJson.product?.variants && showDebugJson.product.variants.length > 0 
                          ? 'text-green-600' 
                          : 'text-yellow-600'
                      }>
                        {showDebugJson.product?.variants && showDebugJson.product.variants.length > 0 ? '✅' : '⚠️'} 
                        Varyantlar: {showDebugJson.product?.variants?.length || 0} adet
                      </li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-indigo-200">
                      <p className="text-xs font-semibold text-indigo-700">
                        💡 Sorun Tespiti:
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        {showDebugJson.product?.attributes && Object.keys(showDebugJson.product.attributes).length > 0 
                          ? '✅ Attributes var, AI bunları görüyor ve kullanıyor'
                          : '❌ Attributes BOŞ! Trendyol API\'den attributes gelmiyor. Bu yüzden AI cevap veremiyor.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {!showDebugJson.raw_trendyol_data && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800">⚠️ Bu soru eski kod ile kaydedildiği için raw_trendyol_data yok. Yeni bir test sorusu sorun.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDebugJson(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
